/* wallCleanup.js — Stage W2 COMMIT-SNAP pipeline (the guarantee half).
 *
 * TWO-REGIME RADII RULING: the preview resolver (drawSnap.js) is screen-px —
 * the FEEL; this module is METRE-BASED and zoom-INDEPENDENT — the GUARANTEE.
 * Correctness cannot depend on how far the user was zoomed: every UI-committed
 * wall endpoint passes through commitSnapPoint at jointTol() (live per
 * LOCK-W-08, default WALL_THICKNESS + 0.05 = 0.2 m), compared INCLUSIVELY
 * (<=). This applies even when the preview snap never visually fired.
 *
 * ORDER (both inclusive at tol = jointTol()):
 *   1 endpoint-cluster capture — nearest wall-graph node point (exact node
 *     coordinate; kills near-node crescents and, with the wallGraph inclusive
 *     flip, closes the exactly-jointTol knife-edge from the UI side);
 *   2 along-host end band — projection onto a host centerline whose
 *     along-parameter is within [-tol, +tol] of 0 or L snaps INTO that end's
 *     NODE (kills the unowned end-band crescent — W1 handoff item 2);
 *   3 host-centerline perpendicular projection — |offset| <= tol lands the
 *     point EXACTLY on the axis (kills the off-axis tee — W1 handoff item 1).
 *
 * NO-STATE-SPLITTING CONTRACT: commit-snap mutates ONLY the not-yet-committed
 * wall's own endpoints; it NEVER writes to any existing wall. Derived T-splits
 * stay derived (wallGraph fingerprint memo), placements/wallOpenings anchors
 * (host wallId + absLeft/u) are safe because hosts are untouched, undo
 * discipline and serialization are unchanged — snapped coordinates are just
 * coordinates in the existing wall schema.
 *
 * addWall/addBox/addArc keep RAW semantics (imports/tests/API may place raw
 * geometry deliberately); the UI paths and state.addWallSnapped opt in here.
 * Pure, zero DOM.
 */
import { getWallGraph, jointTol } from "./wallGraph";

const MIN_WALL_LEN = 0.01; // engine-wide degenerate-wall threshold (wallGraph)
const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/**
 * commitSnapPoint(p, walls, { excludeWallIds }) →
 *   { p, snapped, kind:'node'|'end-band'|'centerline'|null, refWallId? }
 * tol = jointTol() read at CALL time (LOCK-W-08), all comparisons INCLUSIVE.
 */
export function commitSnapPoint(p, walls, opts = {}) {
  const tol = jointTol();
  const raw = [p[0], p[1]];
  const excl = new Set(opts.excludeWallIds || []);
  const list = (walls || []).filter(
    (w) => w && Array.isArray(w.start) && Array.isArray(w.end) && !excl.has(w.id)
  );
  const graph = getWallGraph(walls || []);
  const nodeUsable = (n) =>
    !(excl.size && n.ends && n.ends.length && n.ends.every((e) => excl.has(e.wallId)));

  /* 1 — endpoint-cluster capture (<= tol; the cluster owns the knife-edge tie) */
  let bestN = null, bestD = tol;
  (graph.nodes || []).forEach((n) => {
    if (!nodeUsable(n)) return;
    const d = dist(raw, n.p);
    if (d <= bestD) { bestD = d; bestN = n; }
  });
  if (bestN) return { p: [bestN.p[0], bestN.p[1]], snapped: true, kind: "node" };

  /* 2 — along-host end band → that end's NODE; 3 — centerline projection */
  const nodeOfEnd = (wallId, end) =>
    (graph.nodes || []).find((n) => n.ends && n.ends.some((e) => e.wallId === wallId && e.end === end));
  let band = null, bandD = Infinity;
  let axis = null, axisD = tol;
  list.forEach((w) => {
    if (w.type === "arc") return; // arc endpoints get no centerline commit-snap in W2 (arcScopeRuling)
    const L = dist(w.start, w.end);
    if (L < MIN_WALL_LEN) return;
    const ux = (w.end[0] - w.start[0]) / L, uz = (w.end[1] - w.start[1]) / L;
    const rx = raw[0] - w.start[0], rz = raw[1] - w.start[1];
    const t = rx * ux + rz * uz;
    const off = Math.abs(rz * ux - rx * uz);
    if (off > tol) return;
    const tryEnd = (endName, endPt) => {
      const n = nodeOfEnd(w.id, endName);
      const q = n && nodeUsable(n) ? n.p : endPt;
      const d = dist(raw, q);
      if (d < bandD) { bandD = d; band = { p: q, refWallId: w.id }; }
    };
    if (t >= -tol && t <= tol) tryEnd("start", w.start);
    else if (t >= L - tol && t <= L + tol) tryEnd("end", w.end);
    else if (t > tol && t < L - tol && off <= axisD) {
      axisD = off;
      axis = { p: [w.start[0] + ux * t, w.start[1] + uz * t], refWallId: w.id };
    }
  });
  if (band) return { p: [band.p[0], band.p[1]], snapped: true, kind: "end-band", refWallId: band.refWallId };
  if (axis) return { p: [axis.p[0], axis.p[1]], snapped: true, kind: "centerline", refWallId: axis.refWallId };
  return { p: raw, snapped: false, kind: null };
}

/**
 * commitSnapEndConstrained(start, end, walls, { fix, excludeWallIds }) →
 *   { p, snapped, kind, refWallId? } — Stage W3 TYPED-EDIT commit-snap.
 * The WallPropsPanel typed-value discipline: the typed DIMENSION stays EXACT
 * while the free endpoint may still land on a snap target.
 *   fix:'length' — |end−start| is the typed length: the DIRECTION rotates
 *     toward the commitSnapPoint target, the length is re-applied exactly
 *     (end = start + unit(target−start)·L).
 *   fix:'angle'  — the direction is the typed angle: the target is projected
 *     onto the fixed ray (length adjusts, angle byte-exact).
 * Guards: 50 mm floor on the fixed/derived length (no degenerate walls from a
 * snap), and the constrained result must stay within jointTol() of the typed
 * end (tol-reject — a snap that would visibly move the typed geometry is not
 * a cleanup, it is a surprise). Unsnapped/rejected → the typed end verbatim.
 */
export function commitSnapEndConstrained(start, end, walls, opts = {}) {
  const fix = opts.fix === "angle" ? "angle" : "length";
  const raw = [end[0], end[1]];
  const none = { p: raw, snapped: false, kind: null };
  const L = dist(start, end);
  if (!(L >= 0.05)) return none; // 50 mm floor (wp-length's own minimum)
  const r = commitSnapPoint(end, walls, { excludeWallIds: opts.excludeWallIds });
  if (!r.snapped) return none;
  let cand;
  if (fix === "length") {
    const dq = dist(start, r.p);
    if (dq < MIN_WALL_LEN) return none;
    cand = [
      start[0] + ((r.p[0] - start[0]) / dq) * L,
      start[1] + ((r.p[1] - start[1]) / dq) * L,
    ];
  } else {
    const ux = (end[0] - start[0]) / L, uz = (end[1] - start[1]) / L;
    const t = (r.p[0] - start[0]) * ux + (r.p[1] - start[1]) * uz;
    if (!(t >= 0.05)) return none; // 50 mm floor on the derived length
    cand = [start[0] + ux * t, start[1] + uz * t];
  }
  if (dist(cand, raw) > jointTol()) return none; // tol-reject
  return { p: cand, snapped: true, kind: r.kind, refWallId: r.refWallId };
}

/**
 * commitSnapWall(start, end, walls, opts) → { start, end, snapped, startSnap,
 * endSnap, degenerate }. Applies commitSnapPoint to both endpoints (opt out per
 * end via opts.snapStart / opts.snapEnd — the chain-guard hooks: startBind /
 * close / close-node joints and typed-length ends are already exact or
 * explicitly user-specified). Returns the ORIGINALS unchanged if the snapped
 * segment would be shorter than MIN_WALL_LEN — never emits a degenerate wall.
 */
export function commitSnapWall(start, end, walls, opts = {}) {
  const rs = opts.snapStart !== false
    ? commitSnapPoint(start, walls, opts)
    : { p: [start[0], start[1]], snapped: false, kind: null };
  const re = opts.snapEnd !== false
    ? commitSnapPoint(end, walls, opts)
    : { p: [end[0], end[1]], snapped: false, kind: null };
  if ((rs.snapped || re.snapped) && dist(rs.p, re.p) < MIN_WALL_LEN)
    return {
      start: [start[0], start[1]], end: [end[0], end[1]],
      snapped: false, degenerate: true, startSnap: rs, endSnap: re,
    };
  return { start: rs.p, end: re.p, snapped: rs.snapped || re.snapped, degenerate: false, startSnap: rs, endSnap: re };
}
